import React from 'react';
import { useSeo } from '../hooks/useSeo';
import { Info, FileText, ClipboardCheck, FolderCheck, RefreshCw } from 'lucide-react';
import { FinalContact } from '../components/FinalContact';
const CARDS = [{
  icon: FileText,
  title: 'Broker Packets & Rate Cons',
  desc: 'Help organizing broker packets, rate cons, and load paperwork so documents are ready when brokers need them.'
}, {
  icon: FolderCheck,
  title: 'BOLs & Billing Documents',
  desc: 'Keep BOLs and billing documents organized so money does not get stuck because paperwork is missing or unreadable.'
}, {
  icon: ClipboardCheck,
  title: 'Verification Steps',
  desc: 'Support with carrier profile setup, portals, and verification steps so brokers can confirm you faster.'
}, {
  icon: RefreshCw,
  title: 'Compliance-Related Handoffs',
  desc: 'Coordination and documentation support for compliance-related handoffs where appropriate.'
}];
const FAQ = [{
  q: 'Can FSG help with broker setup?',
  a: 'Yes. FSG helps with broker packets, clearing house setup, portals, and verification steps.'
}, {
  q: 'What paperwork does FSG help organize?',
  a: 'Rate cons, BOLs, billing documents, and load paperwork.'
}, {
  q: 'Can FSG help with billing?',
  a: 'FSG helps keep billing documents organized so you can invoice cleanly without missing paperwork.'
}, {
  q: 'Can FSG help with insurance, registration, or compliance?',
  a: 'FSG can help with documentation and coordination. Insurance, registration, and legal/compliance decisions may require the appropriate provider or authority.'
}, {
  q: 'What information does FSG need from me?',
  a: 'Company details, MC/DOT (if applicable), equipment type, and required documents to start setup.'
}];
export function ComplianceBackOffice() {
  useSeo({
    title: 'Compliance & Back Office Support for Carriers | FSG Transport',
    description: 'FSG Transport helps carriers organize broker packets, rate cons, BOLs, billing documents, verification steps, and compliance-related handoffs so trucks keep moving.',
    path: '/compliance-back-office-support'
  });
  return <div className="w-full" data-id="element-396">
      <section className="relative bg-navy text-white pt-36 pb-20 lg:pt-44 lg:pb-28 overflow-hidden" data-id="element-397">
        <div className="absolute inset-0 z-0 opacity-25" data-id="element-398">
          <img src="https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https%3A//assets.cdn.filesafe.space/cMkAnz3QfJpQ1Lp2u0QG/media/69160e58039bdcddece085c9.png" alt="Back office paperwork" className="w-full h-full object-cover" data-id="element-399" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/90 to-navy/60" data-id="element-400" />
        </div>
        <div className="container-custom relative z-10 max-w-3xl" data-id="element-401">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.1] mb-6 tracking-tight font-['Barlow']" data-id="element-402">
            Back Office Support for Carriers Buried in Paperwork, Broker Setup,
            and Billing
          </h1>
          <p className="text-lg text-off-white/80 font-medium leading-relaxed" data-id="element-403">
            FSG helps carriers organize broker packets, rate cons, BOLs, billing
            documents, verification steps, and compliance-related handoffs so
            trucks can keep moving and paperwork does not slow down money.
          </p>
        </div>
      </section>

      <section className="section-padding bg-off-white" data-id="element-404">
        <div className="container-custom" data-id="element-405">
          <div className="grid md:grid-cols-2 gap-6 mb-10" data-id="element-406">
            {CARDS.map((card, i) => <div key={i} className="bg-white border border-steel rounded-sm p-8 flex gap-5 h-full" data-id="element-407">
                <div className="w-12 h-12 bg-navy rounded-sm flex items-center justify-center shrink-0" data-id="element-408">
                  <card.icon className="w-6 h-6 text-white" data-id="element-409" />
                </div>
                <div data-id="element-410">
                  <h2 className="text-lg font-bold text-navy mb-2" data-id="element-411">
                    {card.title}
                  </h2>
                  <p className="text-body font-medium leading-relaxed" data-id="element-412">
                    {card.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white border-t border-steel" data-id="element-413">
        <div className="container-custom grid md:grid-cols-3 gap-10" data-id="element-414">
          <div data-id="element-415">
            <h3 className="text-xl font-black text-navy mb-4 font-['Barlow']" data-id="element-416">
              Where Paperwork Slows Carriers Down
            </h3>
            <p className="text-body font-medium leading-relaxed" data-id="element-417">
              BOLs, rate cons, broker packets, billing documents, portals,
              unreadable documents, and missing information stall cash flow. FSG
              helps organize this.
            </p>
          </div>
          <div data-id="element-418">
            <h3 className="text-xl font-black text-navy mb-4 font-['Barlow']" data-id="element-419">
              Broker Setup & Verification
            </h3>
            <p className="text-body font-medium leading-relaxed" data-id="element-420">
              Broker packets, portals, clearing houses, and verification steps
              are handled cleanly so you look verifiable and ready to work.
            </p>
          </div>
          <div data-id="element-421">
            <h3 className="text-xl font-black text-navy mb-4 font-['Barlow']" data-id="element-422">
              Billing, Rate Cons & Document Follow-Up
            </h3>
            <p className="text-body font-medium leading-relaxed" data-id="element-423">
              Organized documents help reduce delays. FSG ensures rate cons and
              BOLs are tracked and ready for billing.
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 bg-navy text-white" data-id="element-424">
        <div className="container-custom max-w-4xl" data-id="element-425">
          <div className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-sm p-6" data-id="element-426">
            <Info className="w-6 h-6 text-amber shrink-0 mt-0.5" data-id="element-427" />
            <div data-id="element-428">
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-429">
                Important Compliance Note
              </h3>
              <p className="text-off-white/80 font-medium leading-relaxed" data-id="element-430">
                FSG helps with process and documentation support. This is not
                legal advice, insurance advice, or guaranteed regulatory
                compliance.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-off-white border-t border-steel" data-id="element-431">
        <div className="container-custom max-w-3xl" data-id="element-432">
          <h2 className="text-3xl font-black text-navy mb-8 tracking-tight" data-id="element-433">
            Back Office FAQ
          </h2>
          <div className="space-y-5" data-id="element-434">
            {FAQ.map((f, i) => <div key={i} className="border-l-2 border-red pl-5" data-id="element-435">
                <h3 className="font-bold text-navy mb-1" data-id="element-436">{f.q}</h3>
                <p className="text-body font-medium leading-relaxed" data-id="element-437">{f.a}</p>
              </div>)}
          </div>
        </div>
      </section>

      <FinalContact data-id="element-438" />
    </div>;
}